import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  getSavedVocabulary,
  saveVocabularyItem,
  deleteVocabularyItem,
  type SavedVocabularyItem,
  type SavedVocabularyItemInput,
} from '@/services/api/vocabulary.api';
import type { VocabItem } from '@/types/vocabulary.types';

interface SavedVocabularyContextType {
  savedItems: SavedVocabularyItem[];
  isLoading: boolean;
  saveItem: (item: SavedVocabularyItemInput) => Promise<void>;
  /**
   * Guarda una ficha de vocabulario aprendida (Progreso). No duplica si la
   * palabra ya está en la colección.
   */
  saveLearnedVocab: (item: VocabItem) => Promise<void>;
  removeItem: (word: string) => Promise<void>;
  removeItemById: (id: string) => Promise<void>;
  isSaved: (word: string) => boolean;
}

const SavedVocabularyContext = createContext<SavedVocabularyContextType | null>(null);

export function SavedVocabularyProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [savedItems, setSavedItems] = useState<SavedVocabularyItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const savedItemsRef = useRef(savedItems);
  savedItemsRef.current = savedItems;

  useEffect(() => {
    if (!isAuthenticated) {
      setSavedItems([]);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    getSavedVocabulary()
      .then((items) => {
        if (!cancelled) setSavedItems(items);
      })
      .catch((err) => {
        console.error('[SavedVocabulary] Failed to load saved vocabulary:', err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const saveItem = useCallback(async (input: SavedVocabularyItemInput) => {
    const optimisticItem: SavedVocabularyItem = {
      id: `optimistic-${Date.now()}`,
      userId: '',
      savedAt: Date.now(),
      ...input,
    };
    setSavedItems((prev) => [optimisticItem, ...prev]);

    try {
      const saved = await saveVocabularyItem(input);
      setSavedItems((prev) =>
        prev.map((item) => (item.id === optimisticItem.id ? saved : item))
      );
    } catch (err) {
      console.error('[SavedVocabulary] Failed to save item:', err);
      setSavedItems((prev) => prev.filter((item) => item.id !== optimisticItem.id));
    }
  }, []);

  const saveLearnedVocab = useCallback(async (item: VocabItem) => {
    const w = item.word.trim().toLowerCase();
    const dup = savedItemsRef.current.some((s) => s.word.trim().toLowerCase() === w);
    if (dup) return;
    await saveItem({
      word: item.word,
      partOfSpeech: item.partOfSpeech?.trim() || undefined,
      definition: item.definitionEn?.trim() || '',
      example: item.exampleSentence?.trim() || '',
      wordAudioUrl: item.wordAudioUrl ?? undefined,
    });
  }, [saveItem]);

  const removeItem = useCallback(async (word: string) => {
    const prevItems = savedItemsRef.current;
    setSavedItems((prev) => prev.filter((item) => item.word !== word));

    try {
      await deleteVocabularyItem(word);
    } catch (err) {
      console.error('[SavedVocabulary] Failed to delete item:', err);
      setSavedItems(prevItems);
    }
  }, []);

  const removeItemById = useCallback(async (id: string) => {
    const target = savedItemsRef.current.find((s) => s.id === id);
    if (!target) return;
    await removeItem(target.word);
  }, [removeItem]);

  const isSaved = useCallback(
    (word: string) =>
      savedItems.some((item) => item.word.trim().toLowerCase() === word.trim().toLowerCase()),
    [savedItems]
  );

  return (
    <SavedVocabularyContext.Provider
      value={{ savedItems, isLoading, saveItem, saveLearnedVocab, removeItem, removeItemById, isSaved }}
    >
      {children}
    </SavedVocabularyContext.Provider>
  );
}

export function useSavedVocabulary(): SavedVocabularyContextType {
  const ctx = useContext(SavedVocabularyContext);
  if (!ctx) {
    throw new Error('useSavedVocabulary must be used within SavedVocabularyProvider');
  }
  return ctx;
}
